import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader, Panel, Button, FormRow, Textarea, Select, Banner, Chip, useToast } from '../ds/index'
import { useApi } from '../lib/useApi'
import { patch, put, post, del } from '../lib/api'
import { money, dateShort } from '../lib/format'
import { LoadingBlock, ErrorBlock } from '../components/Loading'

type Tier = { id: string; key: string; name: string; rank: number; price_cents: number; description: string; active: boolean; category_prices: { category_id: string; price_cents: number }[] }
type Pricing = {
  pricing_mode: 'per_buyer' | 'tiers'; prepaid_discount_bps: number; monthly_minimum_deposit_cents: number; dispute_window_hours: number; grade_window_hours: number
  tiers: Tier[]; categories: { id: string; name: string; default_lane_id?: string | null }[]
  lanes?: Lane[]; job_types?: JobType[]
  price_notice_days?: number
  billing_cycle?: 'monthly' | 'weekly'
  upcoming_price_changes?: PriceChange[]
}
type PriceChange = { id: string; kind: string; description: string; effective_at: string; notified_at: string | null }
type ChangeResult = { scheduled?: boolean; applied?: boolean; effective_at?: string; description?: string; change?: ChangeResult | null }

/** What to tell the admin after a price edit: done now, or scheduled with notice (terms 4.3). */
const changeToast = (r: ChangeResult | undefined, done: string) => {
  const c = r?.change ?? r
  return c?.scheduled ? `Scheduled for ${dateShort(c.effective_at!)}. Every business has been told.` : done
}
type Lane = { id: string; key: string; name: string; rank: number; description: string; rates: { tier_id: string; tier: string; bps: number; price_cents: number }[] }
type JobType = { id: string; key: string; name: string; kind: string; lane_id: string; note: string; active: boolean; category_id: string; category_name: string }

const dollars = (cents: number | null | undefined) => (cents == null ? '' : (cents / 100).toFixed(2).replace(/\.00$/, ''))
/** "$12.50" or "12.5" to cents; null for blank; NaN for anything else. */
const toCents = (v: string) => {
  const t = v.replace(/[$,\s]/g, '')
  if (!t) return null
  return /^\d+(\.\d{1,2})?$/.test(t) ? Math.round(Number(t) * 100) : NaN
}

export default function PricingPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { data, error, reload } = useApi<Pricing>('/api/pricing')
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const discount = data.prepaid_discount_bps / 100
  return (
    <>
      {embedded
        ? <p className="muted" style={{ marginTop: 0 }}>What each tier of lead costs, the discount on money put in up front, and the rules every client buys under.</p>
        : <PageHeader title="Tiers and pricing" subtitle="What each tier of lead costs, the discount on money put in up front, and the rules every client buys under." />}
      {data.pricing_mode !== 'tiers' && (
        <div style={{ marginBottom: 'var(--s-6)' }}>
          <Banner tone="warn" title="Leads are billed at each buyer's own price, not by tier">
            Switch to tiers below to price leads by tier and use prepaid balances.
          </Banner>
        </div>
      )}
      <div className="stack">
        <Rules data={data} onSaved={reload} />
        <CardPayments />
        <Panel>
          <div className="panel-header">
            <h2 className="panel-title">Tiers</h2>
            <span className="muted">Prepaid price is {discount}% less: a {money(10000)} lead costs {money(Math.round(10000 * (1 - discount / 100)))} from a balance.</span>
          </div>
          <p className="muted" style={{ marginTop: 0 }}>Screening reads each tier's description to choose a lead's tier, and clients see it next to the price, so write it the way you would explain it to them.</p>
          <div className="tier-lines">
            {data.tiers.map((t) => <TierRow key={t.id} tier={t} categories={data.categories} discountBps={data.prepaid_discount_bps} onSaved={reload} />)}
          </div>
        </Panel>
        {(data.upcoming_price_changes || []).length > 0 && <UpcomingChanges changes={data.upcoming_price_changes!} onSaved={reload} />}
        {data.pricing_mode === 'tiers' && (data.lanes || []).length > 0 && (
          <Lanes lanes={data.lanes!} jobTypes={data.job_types || []} categories={data.categories} discountBps={data.prepaid_discount_bps} noticeDays={data.price_notice_days || 0} onSaved={reload} />
        )}
        <DiscountCodes />
      </div>
    </>
  )
}

/** One saved rule, read-only: what it is, its value, and what it means in a line. */
function RuleCard({ label, value, meaning }: { label: string; value: string; meaning: string }) {
  return (
    <div className="rule-card">
      <span className="rule-label">{label}</span>
      <span className="rule-value">{value}</span>
      <span className="muted rule-meaning">{meaning}</span>
    </div>
  )
}

/**
 * The rules every client buys under. Shown as they stand, in effect; Edit opens
 * the form, Save applies it to everything from that moment, Cancel puts it back.
 */
function Rules({ data, onSaved }: { data: Pricing; onSaved: () => void }) {
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const [mode, setMode] = useState(data.pricing_mode)
  const [discount, setDiscount] = useState(String(data.prepaid_discount_bps / 100))
  const [minimum, setMinimum] = useState(dollars(data.monthly_minimum_deposit_cents))
  const [hours, setHours] = useState(String(data.dispute_window_hours))
  const [gradeHours, setGradeHours] = useState(String(data.grade_window_hours))
  const [cycle, setCycle] = useState(data.billing_cycle || 'monthly')
  const [busy, setBusy] = useState(false)
  const reset = () => {
    setMode(data.pricing_mode); setDiscount(String(data.prepaid_discount_bps / 100))
    setMinimum(dollars(data.monthly_minimum_deposit_cents)); setHours(String(data.dispute_window_hours)); setGradeHours(String(data.grade_window_hours))
    setCycle(data.billing_cycle || 'monthly')
  }
  useEffect(reset, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const d = Number(discount)
    const min = toCents(minimum)
    const h = Number(hours)
    const g = Number(gradeHours)
    if (!Number.isFinite(d) || d < 0 || d > 90) return toast('Discount must be 0 to 90 percent', 'neg')
    if (min === null || Number.isNaN(min)) return toast('Enter the monthly minimum in dollars', 'neg')
    if (!Number.isInteger(h) || h < 1 || h > 720) return toast('Dispute window must be 1 to 720 hours', 'neg')
    if (!Number.isInteger(g) || g < 1 || g > 336) return toast('Grading window must be 1 to 336 hours', 'neg')
    setBusy(true)
    try {
      await patch('/api/pricing', { pricing_mode: mode, prepaid_discount_bps: Math.round(d * 100), monthly_minimum_deposit_cents: min, dispute_window_hours: h, grade_window_hours: g, billing_cycle: mode === 'tiers' ? cycle : undefined })
      toast('Rules saved and in effect', 'pos')
      setEditing(false)
      onSaved()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }

  const pctOf = data.prepaid_discount_bps / 100
  const hrs = (n: number) => (n % 24 === 0 ? `${n / 24} day${n === 24 ? '' : 's'}` : `${n} hours`)
  if (!editing) {
    return (
      <Panel>
        <div className="panel-header">
          <h2 className="panel-title">Rules <Chip tone="pos" dot>In effect</Chip></h2>
          <Button variant="secondary" onClick={() => setEditing(true)}>Edit rules</Button>
        </div>
        <div className="rule-grid">
          <RuleCard label="Billing" value={data.pricing_mode === 'tiers' ? 'By tier' : 'Flat per buyer'}
                    meaning={data.pricing_mode === 'tiers' ? 'Leads are graded C to A+ and charged at that tier' : "Each buyer pays their own set price"} />
          {data.pricing_mode === 'tiers' && <RuleCard label="When leads are charged" value={data.billing_cycle === 'weekly' ? 'Weekly' : 'Monthly'}
                    meaning={data.billing_cycle === 'weekly' ? 'Claimed leads lock Sunday 11:59 PM and are invoiced: tab credit first, then the card' : 'Charged when graded; past the balance, billed on the 1st'} />}
          <RuleCard label="Prepaid discount" value={`${pctOf}%`} meaning={`${money(10000)} added becomes ${money(Math.round(10000 / (1 - pctOf / 100)))} of lead credit`} />
          <RuleCard label="Monthly minimum" value={money(data.monthly_minimum_deposit_cents)} meaning="The least each client adds every month" />
          <RuleCard label="Dispute window" value={hrs(data.dispute_window_hours)} meaning="How long a client has to ask for a refund" />
          <RuleCard label="Grading window" value={hrs(data.grade_window_hours)} meaning="Longest a lead waits for the conversation before it is graded and charged" />
        </div>
      </Panel>
    )
  }

  const example = toCents(minimum)
  const d = Number(discount)
  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">Edit rules</h2></div>
      <div className="form-grid">
        <FormRow label="How leads are billed" htmlFor="p-mode">
          <Select id="p-mode" value={mode} onChange={(e) => setMode(e.target.value as Pricing['pricing_mode'])}>
            <option value="tiers">By tier, from a prepaid balance</option>
            <option value="per_buyer">A flat price per buyer</option>
          </Select>
        </FormRow>
        {mode === 'tiers' && (
          <FormRow label="When leads are charged" htmlFor="p-cycle" help={cycle === 'weekly' ? 'Business Terms v1.3: claim, the Sunday lock, a weekly invoice and a monthly tab' : undefined}>
            <Select id="p-cycle" value={cycle} onChange={(e) => setCycle(e.target.value as 'monthly' | 'weekly')}>
              <option value="weekly">Weekly: claim, Sunday lock, weekly invoice, monthly tab</option>
              <option value="monthly">Monthly: charged when graded, balance past credit billed on the 1st</option>
            </Select>
          </FormRow>
        )}
        <FormRow label="Prepaid discount, percent" htmlFor="p-disc" help={example && Number.isFinite(d) && d < 100 ? `${money(example)} put in becomes ${money(Math.round(example / (1 - d / 100)))} of lead credit` : undefined}>
          <input id="p-disc" className="field" inputMode="decimal" value={discount} onChange={(e) => setDiscount(e.target.value)} />
        </FormRow>
        <FormRow label="Monthly minimum each client puts in, dollars" htmlFor="p-min">
          <input id="p-min" className="field" inputMode="decimal" value={minimum} onChange={(e) => setMinimum(e.target.value)} />
        </FormRow>
        <FormRow label="Dispute window, hours" htmlFor="p-hours" help="How long after a lead arrives a client can ask for the money back">
          <input id="p-hours" className="field" inputMode="numeric" value={hours} onChange={(e) => setHours(e.target.value)} />
        </FormRow>
        <FormRow label="Grading window, hours" htmlFor="p-grade" help="After a rep's first reply, the longest a lead waits for the conversation before it is graded and charged">
          <input id="p-grade" className="field" inputMode="numeric" value={gradeHours} onChange={(e) => setGradeHours(e.target.value)} />
        </FormRow>
      </div>
      <p className="muted" style={{ fontSize: 13 }}>Saving applies these straight away to every new lead and payment. Credit clients already have and leads already charged do not change.</p>
      <div className="row-tight">
        <Button onClick={save} disabled={busy}>{busy ? 'Saving...' : 'Save and apply'}</Button>
        <Button variant="ghost" onClick={() => { reset(); setEditing(false) }} disabled={busy}>Cancel</Button>
      </div>
    </Panel>
  )
}

/** A tier as it stands; Edit opens its form in place. */
function TierRow({ tier, categories, discountBps, onSaved }: { tier: Tier; categories: Pricing['categories']; discountBps: number; onSaved: () => void }) {
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(tier.name)
  const [price, setPrice] = useState(dollars(tier.price_cents))
  const [desc, setDesc] = useState(tier.description)
  const [showCats, setShowCats] = useState(tier.category_prices.length > 0)
  const [busy, setBusy] = useState(false)
  const reset = () => { setName(tier.name); setPrice(dollars(tier.price_cents)); setDesc(tier.description) }
  useEffect(reset, [tier]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const c = toCents(price)
    if (c === null || Number.isNaN(c)) return toast('Enter the price in dollars', 'neg')
    if (!name.trim()) return toast('Give the tier a name', 'neg')
    setBusy(true)
    try {
      const r = await patch<ChangeResult>(`/api/tiers/${tier.id}`, { name, price_cents: c, description: desc })
      toast(changeToast(r, `${name} saved and in effect`), 'pos')
      setEditing(false)
      onSaved()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  const toggleActive = async () => {
    try { await patch(`/api/tiers/${tier.id}`, { active: !tier.active }); onSaved() } catch (e) { toast((e as Error).message, 'neg') }
  }
  const setCategoryPrice = async (categoryId: string, v: string) => {
    const c = toCents(v)
    if (Number.isNaN(c)) return toast('Enter a price in dollars, or leave it blank', 'neg')
    try { const r = await put<ChangeResult>(`/api/tiers/${tier.id}/prices/${categoryId}`, { price_cents: c }); toast(changeToast(r, 'Saved'), 'pos'); onSaved() } catch (e) { toast((e as Error).message, 'neg') }
  }
  const prepaid = (c: number) => Math.round(c * (1 - discountBps / 10000))
  const overrides = tier.category_prices.length

  if (!editing) {
    return (
      <div className={`tier-line${tier.active ? '' : ' is-retired'}`}>
        <span className={`tier-badge t-${tier.key} is-lg`}>{tier.name}</span>
        <div className="tier-line-body">
          <div className="tier-line-price">
            <strong>{money(tier.price_cents)}</strong>
            <span className="muted">{money(prepaid(tier.price_cents))} prepaid</span>
            {overrides > 0 && <Chip>{overrides} category price{overrides === 1 ? '' : 's'}</Chip>}
            {!tier.active && <Chip tone="warn" dot>Retired</Chip>}
          </div>
          <p className="muted">{tier.description || 'No description yet. Screening reads it to choose the tier, so add one.'}</p>
        </div>
        <Button variant="secondary" onClick={() => setEditing(true)}>Edit</Button>
      </div>
    )
  }

  const listed = toCents(price)
  return (
    <div className="tier-line is-editing">
      <span className={`tier-badge t-${tier.key} is-lg`}>{tier.name}</span>
      <div className="tier-line-body">
        <div className="form-grid">
          <FormRow label="Name" htmlFor={`t-name-${tier.id}`}>
            <input id={`t-name-${tier.id}`} className="field" value={name} maxLength={20} onChange={(e) => setName(e.target.value)} />
          </FormRow>
          <FormRow label="Price, dollars" htmlFor={`t-price-${tier.id}`}
                   help={listed != null && !Number.isNaN(listed) ? `${money(prepaid(listed))} from a prepaid balance` : undefined}>
            <input id={`t-price-${tier.id}`} className="field" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
          </FormRow>
        </div>
        <FormRow label="What makes a lead this tier" htmlFor={`t-desc-${tier.id}`}>
          <Textarea id={`t-desc-${tier.id}`} rows={2} value={desc} maxLength={600} onChange={(e) => setDesc(e.target.value)} />
        </FormRow>
        {categories.length > 0 && <Button variant="ghost" onClick={() => setShowCats((v) => !v)}>{showCats ? 'Hide' : 'Set'} prices by category</Button>}
        {showCats && (
          <div className="stack-tight" style={{ marginTop: 'var(--s-4)' }}>
            <p className="muted" style={{ margin: 0 }}>A price here replaces {tier.name}'s price for that category, and saves when you leave the box. Leave blank to use {money(tier.price_cents)}.</p>
            {categories.map((c) => {
              const own = tier.category_prices.find((cp) => cp.category_id === c.id)
              return (
                <label key={c.id} className="row-tight" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ minWidth: '10rem' }}>{c.name}</span>
                  <input className="field" style={{ maxWidth: '8rem' }} inputMode="decimal" defaultValue={dollars(own?.price_cents)} placeholder={dollars(tier.price_cents)}
                         aria-label={`${tier.name} price for ${c.name}`}
                         onBlur={(e) => { if (e.target.value !== dollars(own?.price_cents)) setCategoryPrice(c.id, e.target.value) }} />
                </label>
              )
            })}
          </div>
        )}
        <div className="row-tight" style={{ flexWrap: 'wrap', marginTop: 'var(--s-4)' }}>
          <Button onClick={save} disabled={busy}>{busy ? 'Saving...' : `Save and apply`}</Button>
          <Button variant="ghost" onClick={() => { reset(); setEditing(false) }} disabled={busy}>Cancel</Button>
          <span style={{ marginLeft: 'auto' }}><Button variant="ghost" onClick={toggleActive}>{tier.active ? 'Retire this tier' : 'Bring back'}</Button></span>
        </div>
      </div>
    </div>
  )
}

type Payments = { ready: boolean; live: boolean; connected: boolean; pending?: boolean; spec_fee_bps?: number; account_id: string | null; connected_at: string | null; missing?: string[]; webhook_url?: string }
const STRIPE_RETURN: Record<string, string> = {
  unfinished: 'Stripe still needs a few details. Click Finish connecting to carry on where you left off.',
  stripe: 'Stripe did not finish connecting. Try again.',
  viewing_as: 'Switch back to your own view to connect Stripe.',
  signin: 'You were not signed in on this address when Stripe sent you back. Sign in here, then click Connect with Stripe again.',
}

/** The company's own Stripe account, so clients can add money by card. Money goes to that account, not to SPEC. */
export function CardPayments() {
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const [returned] = useState(() => ({ ok: params.get('stripe') === 'connected', reviewing: params.get('stripe') === 'reviewing', error: params.get('stripe_error') }))
  // Read the result once, then remove only the Stripe keys (the Settings page keeps its tab in the address).
  useEffect(() => {
    if (!params.get('stripe') && !params.get('stripe_error')) return
    const next = new URLSearchParams(params); next.delete('stripe'); next.delete('stripe_error')
    setParams(next, { replace: true })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const { data, reload } = useApi<Payments>('/api/payments')
  if (!data) return null
  const connect = async () => {
    try { const r = await post<{ url: string }>('/api/payments/connect', {}); window.location.href = r.url } catch (e) { toast((e as Error).message, 'neg') }
  }
  const disconnect = async () => {
    try { await post('/api/payments/disconnect', {}); toast('Disconnected', 'pos'); reload() } catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">Card payments</h2>{data.ready && <Chip>{data.live ? 'Live' : 'Test mode'}</Chip>}</div>
      {returned.ok && <div style={{ marginBottom: 'var(--s-4)' }}><Banner tone="pos" title="Stripe is connected">Clients can now add money by card from their portal.</Banner></div>}
      {returned.reviewing && <div style={{ marginBottom: 'var(--s-4)' }}><Banner tone="neutral" title="Stripe is checking your details">That usually takes a few minutes. Card payments switch on by themselves when Stripe is done; there is nothing else to do.</Banner></div>}
      {returned.error && <div style={{ marginBottom: 'var(--s-4)' }}><Banner tone="neg" title="Not connected">{STRIPE_RETURN[returned.error] || 'Stripe did not connect. Try again.'}</Banner></div>}
      {!data.ready ? (
        <p style={{ margin: 0 }}>Card payments are not switched on for this install yet. SPEC sets them up. Until then, record money in on each client's balance page.
          {data.missing?.length ? <span className="muted"> Missing: {data.missing.join(', ')}. Webhook: {data.webhook_url}</span> : null}</p>
      ) : data.connected ? (
        <div className="row-tight" style={{ justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
          <span>Clients pay into Stripe account {data.account_id}{data.connected_at ? `, connected ${dateShort(data.connected_at)}` : ''}. Each payment is added to their lead credit when it clears.{data.spec_fee_bps ? ` As agreed, ${(data.spec_fee_bps / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}% of each card payment goes to SPEC; it shows on each payment in your Stripe dashboard as an application fee.` : ''}</span>
          <Button variant="ghost" onClick={disconnect}>Disconnect</Button>
        </div>
      ) : data.pending ? (
        <div className="row-tight" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="stripe-connect" onClick={connect}>Finish connecting <strong>Stripe</strong></button>
          <span className="muted">Your Stripe account is started. If you have sent Stripe everything, it is checking your details and card payments switch on by themselves; otherwise carry on where you left off.</span>
        </div>
      ) : (
        <div className="row-tight" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="stripe-connect" onClick={connect}>Connect with <strong>Stripe</strong></button>
          <span className="muted">Sets up your company's own Stripe account on Stripe's secure pages, about 10 minutes. Client payments go straight to it.</span>
        </div>
      )}
    </Panel>
  )
}

/** Price changes already scheduled: what, when, and a way to call one off before it applies. */
function UpcomingChanges({ changes, onSaved }: { changes: PriceChange[]; onSaved: () => void }) {
  const toast = useToast()
  const cancel = async (id: string) => {
    try { await del(`/api/price-changes/${id}`); toast('Cancelled. It will not apply.', 'pos'); onSaved() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">Coming price changes</h2>
        <span className="muted">Every business was emailed when each was scheduled.</span>
      </div>
      <div className="stack-tight">
        {changes.map((c) => (
          <div key={c.id} className="row-tight" style={{ justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ flex: '1 1 260px' }}>{c.description}<div className="muted">Takes effect {dateShort(c.effective_at)}{c.notified_at ? '' : ' (businesses not told yet)'}</div></span>
            <Button variant="ghost" onClick={() => cancel(c.id)}>Cancel</Button>
          </div>
        ))}
      </div>
    </Panel>
  )
}

/**
 * Exhibit B: the price of a lead by its conversation tier (C, B, A, A+) and its
 * job tier (Below median, Median, Above median), full price and tab price.
 * Each trade sits in a job tier, and so do its job types. A change waits the
 * notice the terms promise (4.3) and every business is told.
 */
function Lanes({ lanes, jobTypes, categories, discountBps, noticeDays, onSaved }: {
  lanes: Lane[]; jobTypes: JobType[]; categories: Pricing['categories']; discountBps: number; noticeDays: number; onSaved: () => void
}) {
  const toast = useToast()
  const [editing, setEditing] = useState<string | null>(null)
  const [pct, setPct] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const tiers = lanes[0]?.rates || []
  const tab = (c: number) => Math.round((c * (10000 - discountBps)) / 10000)
  const saveRate = async (laneId: string, tierId: string) => {
    const n = Number(pct.replace(/[%\s]/g, ''))
    if (!Number.isFinite(n) || n < 0 || n > 500) { toast('Enter a percentage from 0 to 500', 'neg'); return }
    try {
      const r = await put<ChangeResult>(`/api/lanes/${laneId}/rates/${tierId}`, { bps: Math.round(n * 100) })
      setEditing(null); toast(changeToast(r, 'Price saved'), 'pos'); onSaved()
    } catch (e) { toast((e as Error).message, 'neg') }
  }
  const moveJob = async (id: string, laneId: string) => {
    try { const r = await patch<ChangeResult>(`/api/job-types/${id}`, { lane_id: laneId }); toast(changeToast(r, 'Job tier changed'), 'pos'); onSaved() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  const moveTrade = async (id: string, laneId: string) => {
    try { const r = await patch<ChangeResult>(`/api/categories/${id}/lane`, { lane_id: laneId }); toast(changeToast(r, 'Job tier changed'), 'pos'); onSaved() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">Prices (Exhibit B)</h2>
        <span className="muted">Conversation tier down the side, job tier across. Full price, then tab price.</span>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        {noticeDays > 0
          ? `A change here waits ${noticeDays} days' notice (terms 4.3): every business is emailed, and it applies by itself on the day. A lead already sent keeps its price.`
          : 'A change here applies at once. A lead already sent keeps its price.'} Tap a price to change it.
      </p>
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Tier</th>{lanes.map((l) => <th key={l.id}>{l.name}</th>)}</tr></thead>
          <tbody>
            {[...tiers].reverse().map((t) => (
              <tr key={t.tier_id}>
                <td><strong>{t.tier}</strong></td>
                {lanes.map((l) => {
                  const r = l.rates.find((x) => x.tier_id === t.tier_id)!
                  const k = `${l.id}:${t.tier_id}`
                  return (
                    <td key={k}>
                      {editing === k
                        ? (
                          <form className="row-tight" onSubmit={(e) => { e.preventDefault(); void saveRate(l.id, t.tier_id) }}>
                            <input className="field" style={{ width: 72 }} aria-label={`${l.name} ${t.tier} percentage of the tier price`} value={pct} onChange={(e) => setPct(e.target.value)} autoFocus />
                            <Button type="submit" variant="secondary">Save</Button>
                            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                          </form>
                        )
                        : (
                          <button type="button" className="link-button" onClick={() => { setEditing(k); setPct(String(r.bps / 100)) }} title="Change: a percentage of the tier price">
                            <span className="num">{money(r.price_cents)}</span> <span className="muted num">/ {money(tab(r.price_cents))}</span>
                          </button>
                        )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="stack-tight" style={{ marginTop: 'var(--s-5)' }}>
        <h3 style={{ margin: 0 }}>Each trade's job tier</h3>
        <p className="muted" style={{ margin: 0 }}>Where the trade's job types start, and the price of a lead whose job the screening could not name. Open a trade to move one job type.</p>
        {categories.map((c) => {
          const jobs = jobTypes.filter((j) => j.category_id === c.id)
          return (
            <div key={c.id} style={{ borderTop: '1px solid var(--border)', paddingTop: 'var(--s-3)' }}>
              <div className="row-tight" style={{ justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
                {jobs.length
                  ? <button type="button" className="link-button" aria-expanded={open === c.id} onClick={() => setOpen(open === c.id ? null : c.id)}>{c.name} <span className="muted">({jobs.length} job types)</span></button>
                  : <span>{c.name}</span>}
                <Select aria-label={`Job tier for ${c.name}`} value={c.default_lane_id || ''} onChange={(e) => moveTrade(c.id, e.target.value)}>
                  {!c.default_lane_id && <option value="">Choose a job tier</option>}
                  {lanes.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </Select>
              </div>
              {open === c.id && (
                <div className="stack-tight" style={{ marginTop: 'var(--s-3)', paddingLeft: 'var(--s-4)' }}>
                  {jobs.map((j) => (
                    <div key={j.id} className="row-tight" style={{ justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ flex: '1 1 240px' }}>{j.name}{j.note ? <div className="muted">{j.note}</div> : null}</span>
                      <Select aria-label={`Job tier for ${j.name}`} value={j.lane_id} onChange={(e) => moveJob(j.id, e.target.value)}>
                        {lanes.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </Select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </Panel>
  )
}

type Code = { id: string; code: string; percent_off: number; note: string | null; active: boolean; clients: number; client_list: { id: string; name: string }[]; uses: number; discounted_cents: number }

/** Discount codes: make one, then put it on a client from their page. */
function DiscountCodes() {
  const { data, reload } = useApi<{ codes: Code[] }>('/api/discount-codes')
  const toast = useToast()
  const [code, setCode] = useState('')
  const [pct, setPct] = useState('')
  const [note, setNote] = useState('')
  const make = async () => {
    try {
      await post('/api/discount-codes', { code, percent_off: Number(pct), note })
      toast(`${code.toUpperCase()} made. Put it on a client from their page.`, 'pos'); setCode(''); setPct(''); setNote(''); reload()
    } catch (e) { toast((e as Error).message, 'neg') }
  }
  const toggle = async (c: Code) => {
    try { await patch(`/api/discount-codes/${c.id}`, { active: !c.active }); reload() } catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">Discount codes</h2><span className="muted">put on a client from their page</span></div>
      <p className="muted" style={{ marginTop: 0 }}>A percent off every lead a client is sold. 100% makes their leads free. Turning a code off ends the discount for every client on it.</p>
      <div className="row-tight" style={{ flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 'var(--s-4)' }}>
        <FormRow label="Code"><input className="field" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="PARTNER100" maxLength={30} style={{ width: 160 }} /></FormRow>
        <FormRow label="Percent off"><input className="field" value={pct} onChange={(e) => setPct(e.target.value.replace(/\D/g, ''))} placeholder="100" inputMode="numeric" style={{ width: 90 }} /></FormRow>
        <FormRow label="Note"><input className="field" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Who it is for" maxLength={300} style={{ width: 220 }} /></FormRow>
        <Button onClick={make} disabled={code.trim().length < 2 || !pct}>Make code</Button>
      </div>
      {(data?.codes || []).length === 0 ? <p className="muted" style={{ margin: 0 }}>No codes yet.</p> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Code</th><th className="num">Off</th><th>Clients</th><th className="num">Leads</th><th className="num">Discounted</th><th /></tr></thead>
            <tbody>
              {data!.codes.map((c) => (
                <tr key={c.id}>
                  <td><strong>{c.code}</strong>{c.note ? <div className="muted">{c.note}</div> : null}{!c.active && <div className="muted">Turned off</div>}</td>
                  <td className="num">{c.percent_off}%</td>
                  <td>{c.client_list.length ? c.client_list.map((x) => x.name).join(', ') : <span className="muted">none</span>}</td>
                  <td className="num">{c.uses}</td>
                  <td className="num">{money(c.discounted_cents)}</td>
                  <td><button type="button" className="link-button" onClick={() => toggle(c)}>{c.active ? 'Turn off' : 'Turn on'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}
